import React, { useCallback, useEffect, useState } from "react";
import { Button, Form, Modal, Spinner } from "react-bootstrap";
import axios from "axios";

import { ENERGY_CAPTURE_CONFIG_URL, ENERGY_CAPTURE_PREVIEW_URL } from "./constants";
import { getHeaders } from "./utils";
import RegionPicker from "./region-picker";
import "./energy-capture-modal.css";

const SYMBOLS = ["SPY", "QQQ"];

/**
 * Picks the crop regions the energy capture loop screenshots every two minutes.
 *
 * The readout is drawn onto a canvas, so there is no element to select and no
 * selector to store -- the only way to say where it is, is to point at it on a
 * real render. That is what this is for.
 *
 * Works while the recorder is switched off: the preview endpoint opens its own
 * throwaway browser when no capture session is running, which is the normal case
 * when someone is setting this up.
 */
export default function EnergyCaptureModal({ show, onHide }) {
  const [config, setConfig] = useState(null);
  const [status, setStatus] = useState(null);
  const [preview, setPreview] = useState(null);
  const [symbol, setSymbol] = useState("SPY");
  const [regions, setRegions] = useState({});
  const [loading, setLoading] = useState(false);
  const [rendering, setRendering] = useState(false);
  const [saving, setSaving] = useState(false);
  const [ocr, setOcr] = useState(null);

  const loadConfig = useCallback(async () => {
    setLoading(true);
    try {
      const response = await axios({
        method: "get",
        url: ENERGY_CAPTURE_CONFIG_URL,
        headers: getHeaders(),
        withCredentials: true,
      });
      setConfig(response.data.config);
      setStatus(response.data.status);
      setRegions(response.data.config.regions || {});
    } catch (error) {
      alert("Failed to load energy capture settings.\n\n" + describe(error));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (show) {
      loadConfig();
      setPreview(null);
      setOcr(null);
    }
  }, [show, loadConfig]);

  const renderPreview = async () => {
    setRendering(true);
    setOcr(null);
    try {
      // A cold render launches Chromium and waits for the studies to paint, so
      // this can legitimately take the better part of a minute.
      const response = await axios({
        method: "get",
        url: ENERGY_CAPTURE_PREVIEW_URL,
        headers: getHeaders(),
        withCredentials: true,
        timeout: 180000,
      });
      setPreview(response.data);
      if (response.data.regions) {
        setRegions(response.data.regions);
      }
    } catch (error) {
      alert("Failed to render the layout.\n\n" + describe(error));
    } finally {
      setRendering(false);
    }
  };

  const testOcr = async () => {
    if (!regions[symbol]) {
      alert(`Pick a region for ${symbol} first.`);
      return;
    }
    setRendering(true);
    try {
      const response = await axios({
        method: "get",
        url: `${ENERGY_CAPTURE_PREVIEW_URL}?symbol=${symbol}&debug_recognized_text=1`,
        headers: getHeaders(),
        withCredentials: true,
        timeout: 180000,
      });
      setOcr(response.data.most_accurate_text || {});
    } catch (error) {
      alert("OCR test failed.\n\n" + describe(error));
    } finally {
      setRendering(false);
    }
  };

  const save = async (patch) => {
    setSaving(true);
    try {
      const response = await axios({
        method: "put",
        url: ENERGY_CAPTURE_CONFIG_URL,
        headers: { ...getHeaders(), "Content-Type": "application/json" },
        withCredentials: true,
        data: patch,
      });
      setConfig(response.data.config);
      setStatus(response.data.status);
      setRegions(response.data.config.regions || {});
      return true;
    } catch (error) {
      alert("Failed to save.\n\n" + describe(error));
      return false;
    } finally {
      setSaving(false);
    }
  };

  const region = regions[symbol];
  const busy = loading || rendering || saving;

  return (
    <Modal show={show} onHide={onHide} size="xl" centered data-bs-theme="dark">
      <Modal.Header closeButton className="bg-dark text-light border-secondary">
        <Modal.Title>Energy Capture Regions</Modal.Title>
      </Modal.Header>
      <Modal.Body className="bg-dark text-light energy-capture-body">
        {!config ? (
          <div className="text-secondary">Loading…</div>
        ) : (
          <>
            <div className="energy-capture-toolbar">
              <Form.Select
                size="sm"
                value={symbol}
                onChange={(e) => setSymbol(e.target.value)}
                style={{ width: 110 }}
              >
                {SYMBOLS.map((s) => (
                  <option key={s} value={s}>
                    {s}
                  </option>
                ))}
              </Form.Select>

              <Button size="sm" variant="primary" onClick={renderPreview} disabled={busy}>
                {rendering ? <Spinner as="span" size="sm" /> : "Load live render"}
              </Button>
              <Button size="sm" variant="secondary" onClick={testOcr} disabled={busy || !region}>
                Test OCR
              </Button>

              <Form.Check
                type="switch"
                id="energy-capture-debug"
                label="Debug (dry run, no DB writes)"
                checked={!!config.debug}
                disabled={busy}
                onChange={() => save({ debug: !config.debug })}
              />

              <span className="energy-capture-status">
                session: {status ? status.state : "?"}
                {config.enabled ? " · enabled" : " · disabled"}
              </span>
            </div>

            {preview ? (
              <RegionPicker
                src={preview.image}
                viewport={preview.viewport}
                panes={preview.panes}
                regions={regions}
                symbol={symbol}
                onPick={(picked) => setRegions({ ...regions, [symbol]: picked })}
              />
            ) : (
              <div className="energy-capture-empty">
                Load a live render, then drag a box tightly around the {symbol} energy readout.
              </div>
            )}

            <div className="energy-capture-readout">
              <span>
                {symbol}:{" "}
                {region
                  ? `x=${region.x} y=${region.y} ${region.width}×${region.height}` +
                    (region.paneIndex === null || region.paneIndex === undefined
                      ? " (viewport)"
                      : ` (pane ${region.paneIndex})`)
                  : "not set"}
              </span>
              {ocr && <span className="energy-capture-ocr">OCR: {JSON.stringify(ocr)}</span>}
            </div>
          </>
        )}
      </Modal.Body>
      <Modal.Footer className="bg-dark border-secondary">
        <Button variant="secondary" onClick={onHide} disabled={saving}>
          Close
        </Button>
        <Button
          variant="primary"
          disabled={busy || !config}
          onClick={async () => {
            if (await save({ regions })) {
              alert("Saved.");
            }
          }}
        >
          {saving ? <Spinner as="span" size="sm" /> : "Save regions"}
        </Button>
      </Modal.Footer>
    </Modal>
  );
}

function describe(error) {
  const details = error.response && error.response.data;
  if (details && details.error) {
    return details.error;
  }
  return error.message || "No error details provided.";
}
